import { type CatalogGeoFacets } from '@utazzo/shared';
import { parseAsString, useQueryState } from 'nuqs';
import { useMemo } from 'react';

import type { DataGridFilter } from '@/components/data-grid';

/**
 * Country / City list filters for an Inventory & Supply master.
 *
 * Every catalog list (activities, transfers, hotels, meals, fixed departures)
 * builds them the same way, from its own `/facets` endpoint - so the dropdowns
 * only ever offer places that master actually uses, and grow as products for
 * new locations are added.
 *
 * The City options narrow to the selected Country: without that, "City" on a
 * multi-country catalog is a few hundred entries to scroll. Cities whose facet
 * row carries no `countryId` are always kept - hiding a place the catalog
 * demonstrably uses would be worse than showing one extra. The other half of
 * that pairing - clearing a City when the Country changes - lives in
 * `useDataGridState.setFilter`, so it holds for every grid.
 */
export interface CatalogGeoFilters {
  countryFilter: DataGridFilter;
  cityFilter: DataGridFilter;
}

/** Splices the Country / City chips in after `afterId` (appended when that id
 *  isn't present). */
export const withGeoFilters = (
  filters: ReadonlyArray<DataGridFilter>,
  country: DataGridFilter,
  city: DataGridFilter,
  afterId?: string,
): ReadonlyArray<DataGridFilter> => {
  if (!afterId || !filters.some((f) => f.id === afterId)) return [...filters, country, city];
  return filters.flatMap((f) => (f.id === afterId ? [f, country, city] : [f]));
};

/**
 * The Country filter's current value, read straight off the URL.
 *
 * Grid state is owned by `useDataGridState`, which a page can only call once -
 * and it needs the filters up front. Reading this one key separately (nuqs
 * shares the underlying subscription, same key, same '' default) lets a page
 * build the narrowed City options BEFORE it hands the filters over.
 */
export const useSelectedCountryId = (): string => {
  const [value] = useQueryState('f.countryId', parseAsString.withDefault(''));
  return value;
};

/**
 * Target-country filter for the masters that are sold FOR a country rather
 * than delivered IN a city - visas and insurance carry `targetCountryId` and
 * no city at all, so this is their equivalent of the City chip.
 *
 * Options come from the geography master (seeded, ~200 rows) rather than a
 * facets endpoint: those two catalogs are small enough that "country with no
 * product yet" is a useful thing to see rather than noise.
 */
export const useTargetCountryFilter = (
  countries: ReadonlyArray<{ id: string; name: string }> | undefined,
): DataGridFilter =>
  useMemo(
    () => ({
      id: 'targetCountryId',
      label: 'Target country',
      widthClass: 'w-52',
      searchable: true,
      options: [
        { value: '', label: 'All countries' },
        ...(countries ?? []).map((c) => ({ value: c.id, label: c.name })),
      ],
    }),
    [countries],
  );

/**
 * Two cities can share a name across countries (Springfield, Hyderabad). When
 * that happens the label carries the country so the two are distinguishable;
 * unique names stay short.
 */
const cityLabels = (
  cities: CatalogGeoFacets['cities'],
  countryNameById: Map<string, string>,
): Map<string, string> => {
  const seen = new Map<string, number>();
  for (const c of cities) seen.set(c.name, (seen.get(c.name) ?? 0) + 1);
  const out = new Map<string, string>();
  for (const c of cities) {
    const country = c.countryId ? countryNameById.get(c.countryId) : undefined;
    out.set(c.id, (seen.get(c.name) ?? 0) > 1 && country ? `${c.name}, ${country}` : c.name);
  }
  return out;
};

export const useCatalogGeoFilters = (
  facets: CatalogGeoFacets | undefined,
  /** The Country filter's current value ('' = All countries). */
  selectedCountryId: string,
): CatalogGeoFilters => {
  const countries = facets?.countries;
  const cities = facets?.cities;

  const countryFilter = useMemo<DataGridFilter>(
    () => ({
      id: 'countryId',
      label: 'Country',
      widthClass: 'w-52',
      searchable: true,
      options: [
        { value: '', label: 'All countries' },
        ...(countries ?? []).map((c) => ({ value: c.id, label: c.name })),
      ],
    }),
    [countries],
  );

  const cityFilter = useMemo<DataGridFilter>(() => {
    const all = cities ?? [];
    const countryNameById = new Map((countries ?? []).map((c) => [c.id, c.name]));
    const visible = selectedCountryId
      ? all.filter((c) => !c.countryId || c.countryId === selectedCountryId)
      : all;
    const labels = cityLabels(visible, countryNameById);
    return {
      id: 'cityId',
      label: 'City',
      widthClass: 'w-52',
      searchable: true,
      options: [
        { value: '', label: 'All cities' },
        ...visible.map((c) => ({ value: c.id, label: labels.get(c.id) ?? c.name })),
      ],
    };
  }, [cities, countries, selectedCountryId]);

  return { countryFilter, cityFilter };
};
